import React, { useRef, useState } from 'react';
import { API_URL, authHeaders } from '../../../utils/api';

// Register som går att slå upp automatiskt — se backend/src/utils/registersok.js
const SOKBARA = {
  sakervatten: 'Säker Vattens register',
  elsakerhetsverket: 'Elsäkerhetsverkets register'
};

const inputKlass = 'w-full min-w-0 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent';

/**
 * Registerlänk för en behörighet.
 *
 * För Säker Vatten och Elsäkerhetsverket anger man företagsnamn och
 * organisationsnummer, och länken till företagets sida slås upp automatiskt när
 * man lämnar fälten. Ger sökningen flera träffar väljer man i en lista. För
 * övriga typer — och när sökningen inte hittar något — klistrar man in länken.
 *
 * @param {string} type - behörighetstyp
 * @param {Object} value - { registerForetag, registerOrgnr, registerUrl }
 * @param {Function} onChange - anropas med de fält som ändrats
 */
const RegisterLankFalt = ({ type, value = {}, onChange }) => {
  const sokbar = !!SOKBARA[type];
  const [status, setStatus] = useState(null); // { typ: 'soker'|'hittad'|'flera'|'ingen'|'fel', traffar, text }
  const [visaManuell, setVisaManuell] = useState(false);
  const senasteSok = useRef('');

  const sok = async () => {
    const orgnr = (value.registerOrgnr || '').trim();
    const namn = (value.registerForetag || '').trim();
    if (!orgnr && namn.length < 2) return;
    const nyckel = `${type}|${orgnr}|${namn}`;
    // Samma uppgifter som förra sökningen — sök inte om vid varje blur
    if (nyckel === senasteSok.current) return;
    senasteSok.current = nyckel;

    setStatus({ typ: 'soker' });
    try {
      const params = new URLSearchParams({ typ: type, orgnr, namn });
      const svar = await fetch(`${API_URL}/builders/registersok?${params}`, { headers: authHeaders() });
      const data = await svar.json();
      if (!svar.ok) throw new Error(data.message || 'Sökningen misslyckades');
      const { vald, traffar } = data.data;
      if (vald) {
        valj(vald);
      } else if (traffar.length > 0) {
        setStatus({ typ: 'flera', traffar });
      } else {
        setStatus({ typ: 'ingen' });
        setVisaManuell(true);
      }
    } catch (err) {
      senasteSok.current = '';
      setStatus({ typ: 'fel', text: err.message });
      setVisaManuell(true);
    }
  };

  const valj = (traff) => {
    onChange({
      registerUrl: traff.url,
      // Registrets stavning vinner — den är vad sidan visar
      registerForetag: traff.namn || value.registerForetag,
      registerOrgnr: traff.orgnr || value.registerOrgnr
    });
    setStatus({ typ: 'hittad' });
  };

  const stoppa = (e) => e.stopPropagation();

  const manuellLank = (
    <div className="flex items-center gap-2">
      <input
        type="text"
        inputMode="url"
        value={value.registerUrl || ''}
        onChange={(e) => onChange({ registerUrl: e.target.value })}
        onClick={stoppa}
        placeholder="https://..."
        className={inputKlass}
      />
      {value.registerUrl && /^https?:\/\//i.test(value.registerUrl) && (
        <a href={value.registerUrl} target="_blank" rel="noopener noreferrer" onClick={stoppa}
          className="text-sm text-blue-600 hover:text-blue-800 flex-shrink-0">
          Öppna
        </a>
      )}
    </div>
  );

  if (!sokbar) {
    return (
      <div onClick={stoppa}>
        <label className="block text-xs font-medium text-gray-600 mb-1">Länk till register (valfritt)</label>
        {manuellLank}
      </div>
    );
  }

  return (
    <div className="space-y-2" onClick={stoppa}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Företag med behörigheten</label>
          <input
            type="text"
            value={value.registerForetag || ''}
            onChange={(e) => onChange({ registerForetag: e.target.value })}
            onBlur={sok}
            placeholder="T.ex. Amperiatech Solutions AB"
            className={inputKlass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Organisationsnummer</label>
          <input
            type="text"
            value={value.registerOrgnr || ''}
            onChange={(e) => onChange({ registerOrgnr: e.target.value })}
            onBlur={sok}
            placeholder="556677-8899"
            className={inputKlass}
          />
        </div>
      </div>

      {status?.typ === 'soker' && (
        <p className="text-xs text-gray-500">Söker i {SOKBARA[type]}…</p>
      )}

      {value.registerUrl && status?.typ !== 'soker' && status?.typ !== 'flera' && (
        <p className="text-xs text-green-700 flex items-center gap-1.5 flex-wrap">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {status?.typ === 'hittad' ? 'Hittad i registret' : 'Länk sparad'}
          <a href={value.registerUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
            Öppna sidan
          </a>
        </p>
      )}

      {status?.typ === 'flera' && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-2">
          <p className="text-xs text-amber-800 mb-1.5">
            {status.traffar.length} träffar — välj rätt företag, eller fyll i organisationsnumret:
          </p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {status.traffar.map(t => (
              <button
                key={t.url}
                type="button"
                onClick={() => valj(t)}
                className="w-full text-left text-xs px-2 py-1.5 bg-white border border-gray-200 rounded hover:border-leadme-pink hover:bg-pink-50"
              >
                {t.namn}{t.orgnr ? ` · ${t.orgnr}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {status?.typ === 'ingen' && (
        <p className="text-xs text-amber-700">Hittade inget företag i {SOKBARA[type]}. Kontrollera uppgifterna eller klistra in länken nedan.</p>
      )}
      {status?.typ === 'fel' && (
        <p className="text-xs text-red-600">{status.text}</p>
      )}

      {visaManuell ? (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Länk till register</label>
          {manuellLank}
        </div>
      ) : (
        <button type="button" onClick={() => setVisaManuell(true)} className="text-xs text-gray-500 hover:text-gray-700 underline">
          Klistra in länk själv
        </button>
      )}
    </div>
  );
};

export default RegisterLankFalt;
